import { CustomAxiosRequestConfig } from "@/interfaces/customAxiosRequestConfig";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { computed, reactive, ref, watch } from "vue";

interface Hotel {
  code: string;
  name: string;
  chain: string;
  addressLine: string;
  image: string;
}

interface Props {
  immediate?: boolean;
  queryKey?: string;
  errorHandling?: boolean;
  hotelId: number;
}

interface Params {
  filters: Filters;
  hotelCityCode: string;
  hotelId: number;
}

interface Filters {
  q?: string;
  months?: number;
}

interface setParams {
  hotelId: number;
  code: string;
}

const getAmadeusHotelCodes = async (
  params: Params,
  errorHandling = true,
): Promise<Hotel[]> => {
  const { data } = await axios.get<Hotel[]>(
    `/api/internal/hotels/${params.hotelId}/amadeus/search?cityCode=${params.hotelCityCode}`,
    {
      errorHandling: errorHandling,
      params: params.filters,
    } as CustomAxiosRequestConfig,
  );

  return data;
};

const setAmadeusCode = async (params: setParams) => {
  const { code, hotelId } = params;

  const { data } = await axios.post<Hotel>(
    `/api/internal/hotels/${hotelId}/amadeus`,
    {
      code,
    },
  );
  return data;
};

const useAmadeusHotelSearch = (props: Props) => {
  const { errorHandling } = props;
  const cityCode = ref<string>("");
  const filters = reactive<Filters>({});
  const hotelId = ref<number>(props.hotelId);
  const enable = ref(false);

  const { data, isError, isLoading, refetch, remove, isRefetching } = useQuery(
    ["amadeusCodeSearch", hotelId.value, cityCode.value, filters.q],
    () =>
      getAmadeusHotelCodes(
        {
          hotelCityCode: cityCode.value,
          hotelId: hotelId.value,
          filters: filters,
        },
        errorHandling,
      ),
    {
      enabled: enable,
      keepPreviousData: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );

  const updateHotelMutation = useMutation((params: setParams) =>
    setAmadeusCode(params),
  );

  return {
    data,
    cityCode,
    filters,
    isError,
    isLoading,
    isRefetching,
    enable,
    remove,
    refetch,
    updateHotelMutation,
    hotelId,
  };
};

export default useAmadeusHotelSearch;
